/**
 * Layer capability detection.
 *
 *   Layer → detectLayerSource() → LayerCapabilities → menu builds itself
 *
 * The 3-dot menu renders ONLY the operations reported here, so an option that
 * a service cannot perform is never shown. Adding a new service type means
 * adding a source kind here plus an adapter — the menu itself never changes.
 */

import type { AppModule, Layer, LayerGeometryType, LayerSourceKind } from '../types';

/** Maps an Esri geometry type to our normalised family. */
export function normaliseGeometryType(esriType: string | undefined): LayerGeometryType {
  switch (String(esriType ?? '')) {
    case 'esriGeometryPoint':
    case 'esriGeometryMultipoint':
      return 'point';
    case 'esriGeometryPolyline':
      return 'line';
    case 'esriGeometryPolygon':
    case 'esriGeometryEnvelope':
      return 'polygon';
    default:
      return 'unknown';
  }
}

/** Geometry family for a layer, defaulting to point when unknown. */
export function getLayerGeometryType(layer: Layer): LayerGeometryType {
  return layer.geometryType ?? 'unknown';
}

export interface LayerCapabilities {
  sourceKind: LayerSourceKind;
  /** Human label shown in the Layer Information panel. */
  sourceLabel: string;

  rename: boolean;
  zoomTo: boolean;
  toggleVisibility: boolean;
  opacity: boolean;
  /** Whether this layer's zoom-dependent draw range can be set here — true for
   *  every source, the same as opacity: it is app-state, not a service call. */
  visibleRange: boolean;
  showInLegend: boolean;
  reorder: boolean;
  duplicate: boolean;
  remove: boolean;
  attributeTable: boolean;
  refresh: boolean;
  info: boolean;
  /**
   * True only where the CLIENT draws the geometry. WMS tiles and MapServer
   * export images are rendered server-side, so restyling them would require
   * an SLD (GeoServer) or a dynamicLayers renderer (MapServer) — not supported.
   */
  symbology: boolean;
  /**
   * Whether the layer can be labelled at all, and by which mechanism. These are
   * genuinely different implementations, not one feature with a flag:
   *  - 'client' : the browser holds the features, so labels are drawn onto the
   *               map canvas from the real attribute values.
   *  - 'sld'    : GeoServer renders the tiles, so the label travels to the
   *               server as an SLD TextSymbolizer and comes back burnt into
   *               the image. Server-side labelling is the only thing that can
   *               label WMS features.
   *  - 'none'   : no supported mechanism — the menu entry is not offered.
   */
  labeling: boolean;
  labelingMode: LabelingMode;
  /**
   * Whether a click on this layer can return attributes at all — the pop-up is
   * configuration for the identify path, so a source identify cannot query has
   * nothing to configure. File-backed layers are drawn straight onto the map
   * and are not part of that path yet, so they report false.
   */
  popup: boolean;
}

/** How a layer's labels reach the map. See LayerCapabilities.labelingMode. */
export type LabelingMode = 'client' | 'sld' | 'none';

/** Decides what a layer is from how it was added and which service backs it. */
export function detectLayerSource(layer: Layer, module: AppModule): LayerSourceKind {
  // A file-backed layer owns its data, so it is checked before any service.
  // Raster is checked first: the two fields are mutually exclusive, but a
  // raster layer's `data` (see localFile.ts's `fromGeoTiff`) is a dummy empty
  // collection, not a genuine "no features" vector layer.
  if (layer.localRaster) return 'local-raster';
  if (layer.localData) return 'local';
  if (layer.arcgisLayerId !== undefined) {
    // FeatureServer renders as client-side vectors; MapServer as server images.
    return /\/FeatureServer/i.test(module.arcgisUrl ?? '') ? 'arcgis-feature' : 'arcgis-map';
  }
  return 'wms';
}

const SOURCE_LABELS: Record<LayerSourceKind, string> = {
  'arcgis-feature': 'ArcGIS FeatureServer',
  'arcgis-map': 'ArcGIS MapServer',
  wms: 'GeoServer WMS',
  local: 'Local file',
  'local-raster': 'Local raster (GeoTIFF)',
};

export function getLayerCapabilities(layer: Layer, module: AppModule): LayerCapabilities {
  const sourceKind = detectLayerSource(layer, module);

  // Operations that are purely app-state work for every source.
  const base = {
    sourceKind,
    sourceLabel: SOURCE_LABELS[sourceKind],
    rename: true,
    toggleVisibility: true,
    opacity: true,
    visibleRange: true,
    showInLegend: true,
    reorder: true,
    duplicate: true,
    remove: true,
    refresh: true,
    info: true,
    zoomTo: true,
  };

  switch (sourceKind) {
    case 'local-raster':
      return {
        ...base,
        // A pixel grid, not features — nothing to list in a table or query
        // with a click, and nothing to restyle beyond opacity (already in
        // `base`). Zooming still works: a GeoTIFF's georeferencing IS its
        // bounds, so unlike a WMS layer this never has to wait on a request.
        attributeTable: false,
        popup: false,
        symbology: false,
        refresh: false,
        labeling: false,
        labelingMode: 'none',
      };

    case 'local':
      // A Density-tool result carries the same `localData` shape as any
      // imported layer, but its "features" are synthetic sample points with
      // one `weight` property each — no attribute table, popup, or vector
      // renderer to offer for those. Its colour is set by re-running the
      // tool, not by the Symbology panel.
      if (layer.heatmap) {
        return {
          ...base,
          attributeTable: false,
          popup: false,
          symbology: false,
          refresh: false,
          labeling: false,
          labelingMode: 'none',
        };
      }
      return {
        ...base,
        // The features are already in memory.
        attributeTable: true,
        /* Answered by hit-testing the features in memory rather than by
           querying a service — which is why this used to read `false` and say
           "the identify path never queries it". It does now: see
           LocalFeatureIdentify in MapView. */
        popup: true,
        // Drawn client-side as GeoJSON, so fully restylable.
        symbology: true,
        // Nothing to re-request: there is no service behind it.
        refresh: false,
        labeling: true,
        labelingMode: 'client',
      };

    case 'arcgis-feature':
      return {
        ...base,
        // Vector features are queryable, so attributes are always available.
        attributeTable: true,
        popup: true,
        // Drawn client-side as GeoJSON → fully restylable.
        symbology: true,
        labeling: true,
        labelingMode: 'client',
      };

    case 'arcgis-map':
      return {
        ...base,
        // MapServer sublayers are queryable via /query.
        attributeTable: true,
        popup: true,
        // Server renders the image; client cannot restyle it.
        symbology: false,
        // An `export` image carries whatever labels the service author baked
        // into the map document. Overriding them needs `dynamicLayers`, which
        // this app does not use, so no labelling mechanism is available.
        labeling: false,
        labelingMode: 'none',
      };

    case 'wms':
    default:
      return {
        ...base,
        // WMS itself has no attribute access — this needs the matching WFS
        // endpoint, which the app derives from the same GeoServer base URL.
        attributeTable: Boolean(module.geoServerUrl),
        // GetFeatureInfo answers a click without needing WFS, so the pop-up is
        // configurable even where the attribute table is not.
        popup: true,
        // Restyled server-side by passing a generated SLD via SLD_BODY.
        symbology: true,
        // WMS extents come from GetCapabilities; without stored bounds there is
        // nothing to zoom to until the layer has been (re)loaded.
        zoomTo: Boolean(layer.bounds),
        // Labels are drawn by GeoServer from an SLD TextSymbolizer. The field
        // list behind the UI comes from the matching WFS endpoint, so without a
        // GeoServer URL there is nothing to configure against.
        labeling: Boolean(module.geoServerUrl),
        labelingMode: module.geoServerUrl ? 'sld' : 'none',
      };
  }
}

/** Display name honouring the app-only rename, falling back to the service name. */
export function getLayerDisplayName(layer: Layer): string {
  return layer.displayName?.trim() || layer.name;
}
